import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FileText,
  Search,
  Filter,
  ChevronLeft,
  DollarSign,
  User,
  Calendar,
  Package,
  Send,
  CheckCircle2,
  Clock,
  XCircle,
  Loader2,
  X,
} from "lucide-react";
import { toast } from "react-toastify";
import quoteService from "../../api/QuoteService";
import Loader from "../../components/Loader";

const STATUS_CONFIG = {
  pending: { label: "Pending", color: "bg-amber-100 text-amber-800 border-amber-200", icon: Clock },
  quoted: { label: "Quoted", color: "bg-blue-100 text-blue-800 border-blue-200", icon: DollarSign },
  accepted: { label: "Accepted", color: "bg-green-100 text-green-800 border-green-200", icon: CheckCircle2 },
  rejected: { label: "Rejected", color: "bg-red-100 text-red-800 border-red-200", icon: XCircle },
};

export default function QuoteRequests() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [quotedAmount, setQuotedAmount] = useState("");
  const [submittingQuote, setSubmittingQuote] = useState(false);

  const fetchRequests = useCallback(async () => {
    setLoading(true);
    try {
      const result = await quoteService.getQuoteRequests({ status: statusFilter !== "all" ? statusFilter : undefined });
      const data = result?.data?.data ?? result?.data ?? (Array.isArray(result?.data) ? result.data : []);
      setRequests(Array.isArray(data) ? data : []);
    } catch (err) {
      setRequests([]);
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  const filteredRequests = requests.filter((r) => {
    const matchSearch =
      !searchQuery ||
      (r.policy_name || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
      (r.customer_name || r.consumer_name || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
      (r.package_name || r.product_name || "").toLowerCase().includes(searchQuery.toLowerCase());
    return matchSearch;
  });

  const handleSubmitQuote = async () => {
    if (!selectedRequest?.id) return;
    const amount = parseFloat(quotedAmount);
    if (isNaN(amount) || amount < 0) {
      toast.error("Enter a valid quoted amount");
      return;
    }
    setSubmittingQuote(true);
    try {
      const result = await quoteService.updateQuoteRequest(selectedRequest.id, {
        quoted_amount: amount,
        status: "quoted",
      });
      if (result?.status) {
        toast.success("Quote sent successfully");
        setSelectedRequest(null);
        setQuotedAmount("");
        fetchRequests();
      } else {
        toast.error(result?.message || "Failed to update quote");
      }
    } catch (err) {
      toast.error("Failed to update quote");
    } finally {
      setSubmittingQuote(false);
    }
  };

  const getStatusInfo = (status) => STATUS_CONFIG[status] || STATUS_CONFIG.pending;

  return (
    <div className="min-h-full bg-gray-50">
      <div className="p-6">
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6"
        >
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-xl">
                <FileText className="w-6 h-6 text-amber-600" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Quote Requests</h1>
                <p className="text-sm text-gray-600">View and respond to quote requests with a quoted amount</p>
              </div>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mb-6 p-4 bg-white rounded-xl border border-gray-200 shadow-sm"
        >
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by policy, customer, or package..."
                className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
              />
            </div>
            <div className="flex items-center gap-2">
              <Filter className="w-5 h-5 text-gray-500" />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
              >
                <option value="all">All statuses</option>
                <option value="pending">Pending</option>
                <option value="quoted">Quoted</option>
                <option value="accepted">Accepted</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>
          </div>
        </motion.div>

        {loading ? (
          <Loader text="Loading quote requests..." />
        ) : filteredRequests.length === 0 ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="py-16 text-center bg-white rounded-xl border border-gray-200"
          >
            <FileText className="w-16 h-16 mx-auto text-gray-300 mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No quote requests yet</h3>
            <p className="text-sm text-gray-600 max-w-md mx-auto">
              When consumers request a quote for a quote-based package, requests will appear here. You can then attach a quoted amount for them to accept and pay.
            </p>
          </motion.div>
        ) : (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3"
          >
            {filteredRequests.map((req) => {
              const statusInfo = getStatusInfo(req.status || "pending");
              const StatusIcon = statusInfo.icon;
              return (
                <motion.div
                  key={req.id || req.slug || Math.random()}
                  layout
                  className="p-4 bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow"
                >
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex gap-2 items-center min-w-0">
                      <Package className="w-5 h-5 text-amber-600 shrink-0" />
                      <span className="font-semibold text-gray-900 truncate">
                        {req.package_name || req.product_name || "Package"}
                      </span>
                    </div>
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full border ${statusInfo.color}`}>
                      <StatusIcon className="w-3 h-3" />
                      {statusInfo.label}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 mb-2 truncate">{req.policy_name || "Policy"}</p>
                  <div className="flex gap-2 items-center text-xs text-gray-500 mb-3">
                    <User className="w-4 h-4" />
                    <span>{req.customer_name || req.consumer_name || "Customer"}</span>
                  </div>
                  {req.quoted_amount != null && req.quoted_amount > 0 && (
                    <p className="text-sm font-semibold text-green-700 mb-3">Quoted: ₵{Number(req.quoted_amount).toFixed(2)}</p>
                  )}
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedRequest(req);
                      setQuotedAmount(req.quoted_amount != null ? String(req.quoted_amount) : "");
                    }}
                    className="w-full inline-flex justify-center items-center gap-2 px-3 py-2 text-sm font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-lg hover:bg-amber-100 transition-colors"
                  >
                    <Send className="w-4 h-4" />
                    {(req.status === "quoted" || req.quoted_amount > 0) ? "View / Update Quote" : "Attach Quote"}
                  </button>
                </motion.div>
              );
            })}
          </motion.div>
        )}
      </div>

      {/* Detail / Attach Quote Modal */}
      <AnimatePresence>
        {selectedRequest && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
            onClick={() => !submittingQuote && setSelectedRequest(null)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-4 border-b border-gray-200 flex justify-between items-center shrink-0">
                <h3 className="text-lg font-bold text-gray-900">Quote Request Details</h3>
                <button
                  type="button"
                  onClick={() => !submittingQuote && setSelectedRequest(null)}
                  className="p-2 rounded-lg hover:bg-gray-100"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="flex-1 min-h-0 overflow-y-auto">
                <div className="p-4 space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div>
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Policy</p>
                      <p className="text-gray-900 font-medium mt-0.5">{selectedRequest.policy_name || "—"}</p>
                    </div>
                    <div>
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Package</p>
                      <p className="text-gray-900 font-medium mt-0.5">{selectedRequest.package_name || selectedRequest.product_name || "—"}</p>
                    </div>
                    <div>
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Customer</p>
                      <p className="text-gray-900 font-medium mt-0.5">{selectedRequest.customer_name || selectedRequest.consumer_name || "—"}</p>
                    </div>
                  </div>

                  {selectedRequest.form_data && Object.keys(selectedRequest.form_data).length > 0 && (
                    <div className="border border-gray-200 rounded-xl overflow-hidden">
                      <div className="px-4 py-2.5 bg-gray-50 border-b border-gray-200">
                        <p className="text-sm font-semibold text-gray-700">
                          Customer answers ({Object.keys(selectedRequest.form_data).length} {Object.keys(selectedRequest.form_data).length === 1 ? "field" : "fields"})
                        </p>
                      </div>
                      <div className="max-h-[min(50vh,24rem)] overflow-y-auto">
                        <div className="divide-y divide-gray-100">
                          {Object.entries(selectedRequest.form_data).map(([key, value]) => {
                            const label = (selectedRequest.quote_request_fields || []).find((f) => f.key === key)?.label ||
                              key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
                            const displayValue = value != null && value !== "" ? String(value) : "—";
                            return (
                              <div key={key} className="px-4 py-3 hover:bg-gray-50/50">
                                <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-0.5">{label}</p>
                                <p className="text-sm text-gray-900 break-words whitespace-pre-wrap">{displayValue}</p>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="pt-2 pb-1">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Quoted amount (₵)</label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={quotedAmount}
                      onChange={(e) => setQuotedAmount(e.target.value)}
                      placeholder="0.00"
                      className="w-full max-w-xs px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                    />
                  </div>
                </div>
              </div>
              <div className="p-4 border-t border-gray-200 flex gap-2 justify-end shrink-0 bg-gray-50/50">
                <button
                  type="button"
                  onClick={() => setSelectedRequest(null)}
                  disabled={submittingQuote}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSubmitQuote}
                  disabled={submittingQuote || !quotedAmount}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white bg-amber-500 rounded-lg hover:bg-amber-600 disabled:opacity-50"
                >
                  {submittingQuote ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  {selectedRequest.quoted_amount != null ? "Update Quote" : "Send Quote"}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
